<script setup lang="ts">
import { useToast } from '@solfacil/girassol';

import {
  useGetPaymentInfoLazyQuery,
  type GetPaymentInfoOut
} from '~/types/ecommerce/generated.codegen';
const { createToast } = useToast();

const props = defineProps<{
  open: boolean;
  orderId: string;
}>();

defineEmits<{
  (event: 'close'): void;
}>();

const paymentInfo = ref<GetPaymentInfoOut>();

const {
  onResult: getPaymentResult,
  load: getPaymentInfo,
  refetch: refetchPayments,
  loading: getPaymentLoading
} = useGetPaymentInfoLazyQuery();

getPaymentResult((result) => {
  paymentInfo.value = result?.data?.getPaymentInfo ?? undefined;
});

watch(
  () => props.open,
  async (open) => {
    if (open) {
      await getPaymentInfo(null, { orderId: String(props.orderId) });
    }
  }
);

const isPixPayment = computed(() => paymentInfo.value?.type === 'PIX');

function copyPixCodeToClipboard () {
  const text = paymentInfo.value?.detail?.text;
  if (text) {
    useClipboard().copy(text);
    createToast({
      type: 'success',
      description: 'Código pix copiado para a área de transferência'
    });
  }
}

const getModalTitle = computed(() => {
  if (!paymentInfo.value) {
    return 'Pagamento';
  }
  return `Pagamento ${isPixPayment.value ? 'via Pix' : 'via Boleto'}`;
});

function downloadBoleto () {
  navigateTo(paymentInfo.value?.detail?.file as string, {
    external: true,
    open: { target: '_blank' }
  });
}
</script>

<template>
  <SolModal
    id="payment-modal"
    :is-open="open"
    :title="getModalTitle"
    :size="{ desktop: 'medium', mobile: 'bottom-sheet' }"
    @close="$emit('close')"
  >
    <template v-if="!getPaymentLoading" #action-primary>
      <SolButton
        v-if="!paymentInfo"
        id="refetch-payments"
        variant="primary"
        size="medium"
        :loading="getPaymentLoading"
        @click="refetchPayments({ orderId: orderId })"
      >
        <template #icon:left>
          <icon-girassol:autorenew />
        </template>
        Tentar novamente
      </SolButton>

      <SolButton
        v-else-if="isPixPayment"
        id="copy-pix-code"
        variant="primary"
        size="medium"
        @click="copyPixCodeToClipboard"
      >
        <template #icon:left>
          <icon-girassol:content-copy />
        </template>
        Copiar código pix
      </SolButton>

      <SolButton
        v-else
        id="download-boleto"
        variant="primary"
        size="medium"
        @click="downloadBoleto"
      >
        <template #icon:left>
          <icon-girassol:download />
        </template>
        Download do boleto
      </SolButton>
    </template>

    <template #action-secondary>
      <SolButton
        id="close-modal"
        variant="secondary"
        size="medium"
        @click="$emit('close')"
      >
        Fechar
      </SolButton>
    </template>

    <SharedCircleLoader v-if="getPaymentLoading" />

    <SolEmptyState
      v-else-if="!paymentInfo"
      id="no-orders"
      variant="empty-list"
      title="Erro ao trazer dados do pagamento"
      subtitle="Tenta novamente mais tarde ou entre em contato com o suporte."
    />

    <div v-else class="payment-modal-content">
      <div class="payment-title">
        <span class="order-price">Valor do pedido <b>{{ paymentInfo?.value }}</b></span>
        <span
          v-if="paymentInfo.detail?.expiration_date"
          class="subtitle"
        >{{ isPixPayment ? "Pix" : "Boleto" }} válido até o dia
          {{ paymentInfo.detail.expiration_date || "" }}</span>
      </div>

      <SharedImgLazy
        v-if="!isPixPayment"
        src="/images/payment.svg"
        alt="payment-image"
        class="payment-image"
      />
      <SharedImgLazy
        v-else
        :src="paymentInfo?.detail?.file as string"
        src-placeholder="/images/empty-image.jpg"
        alt="pix-image"
        class="pix-image"
      />
      <SolInputText
        v-if="isPixPayment"
        id="input-pix-code"
        name="input-pix-code"
        class="input-pix-code"
        :value="paymentInfo?.detail?.text!"
        readonly
        @click="copyPixCodeToClipboard"
      />

      <div v-if="!isPixPayment" class="boleto-info">
        <span class="boleto-title">Faça o download do PDF do boleto para fazer o pagamento</span>
        <span class="subtitle">O pagamento do boleto pode levar até 3 dias para ser validado.</span>
      </div>

      <div v-else>
        <span class="subtitle">O pagamento do Pix pode levar até <b>15min</b> para ser atualizado na
          plataforma.</span>
        <SolDivider thickness="x-small" class="divider" />
        <div class="pix-infos">
          <span class="pix-info-title">Para fazer o pagamento do Pix</span>
          <span class="pix-info">
            <span>1. Entre no app da sua instituição financeira e acesse o ambiente
              Pix.</span>
            <span>2. Escolha a opção QR Code.</span>
            <span>3. Escaneie o código.</span>
          </span>

          <span class="pix-info-title">Ou copie e cole o código Pix</span>
          <span class="pix-info">
            No mesmo app da sua instituição financeira, no ambiente Pix, escolha
            a opção de colar código Pix
          </span>
        </div>
      </div>
    </div>
  </SolModal>
</template>

<style lang="scss" scoped>
.payment-modal-content {
  @apply flex flex-col items-center min-w-full gap-4xs;

  .payment-title {
    @apply flex flex-col items-center gap-quark;

    .order-price {
      @apply fonts-body-x-large-regular;
    }
  }

  .subtitle {
    @apply fonts-body-large-regular text-neutral-low-light text-center block;
  }

  .payment-image {
    @apply w-40;
  }

  .pix-image {
    @apply w-48;
  }

  .input-pix-code {
    @apply w-full;
  }

  .boleto-info {
    @apply flex flex-col items-center gap-quark;

    .boleto-title {
      @apply fonts-body-large-bold text-brand-primary-dark text-center;
    }
  }

  .divider {
    @apply my-4xs;
  }

  .pix-infos {
    @apply flex flex-col gap-quark;

    .pix-info-title {
      @apply fonts-body-medium-bold text-brand-primary-dark mt-quark;
    }

    .pix-info {
      @apply flex flex-col fonts-body-medium-regular text-neutral-low-light;
    }
  }
}
</style>
